import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../api/api';
import { toast } from 'sonner';

const AdminPanel = () => {
  const [selectedSequence, setSelectedSequence] = useState('sequence1');
  const [templates, setTemplates] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(false);
  
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    delayHours: 24,
    buttonText: '',
    buttonLink: ''
  });

  const sequences = [
    { value: 'sequence1', label: 'Sequence 1 - Monthly Magazine' },
    { value: 'sequence2', label: 'Sequence 2 - Digital Report (Offer 2)' },
    { value: 'sequence3', label: 'Sequence 3 - Digital Report (Offer 3)' },
    { value: 'sequence4', label: 'Sequence 4 - Digital Course' },
    { value: 'sequenceNoCheckout', label: 'Sequence 5 - Cart Abandonment' }
  ];

  useEffect(() => {
    fetchTemplates();
  }, [selectedSequence]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await api.getEmailTemplates(selectedSequence);
      setTemplates(data);
      if (data.length > 0) {
        selectEmail(data[0]);
      } else {
        setSelectedEmail(null);
        setFormData({
          subject: '',
          content: '',
          delayHours: 24,
          buttonText: '',
          buttonLink: ''
        });
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const selectEmail = (template) => {
    setSelectedEmail(template);
    setFormData({
      subject: template.subject,
      content: template.content,
      delayHours: template.delayHours,
      buttonText: template.buttonText,
      buttonLink: template.buttonLink
    });
  };

  const handleSave = async () => {
    if (!selectedEmail) {
      toast.error('No email selected');
      return;
    }

    setSaving(true);
    try {
      await api.updateEmailTemplate(
        selectedSequence,
        selectedEmail.emailNumber,
        formData
      );
      toast.success(`Email ${selectedEmail.emailNumber} updated successfully!`);
      await fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleInitialize = async () => {
    if (!window.confirm('This will create placeholder templates for all email sequences. Continue?')) {
      return;
    }

    setInitializing(true);
    try {
      const result = await api.initializeEmailTemplates();
      toast.success(result.message);
      await fetchTemplates();
    } catch (error) {
      console.error('Error initializing templates:', error);
      toast.error('Failed to initialize templates');
    } finally {
      setInitializing(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h2 className="text-4xl font-bold mb-2" style={{color: '#3D2817'}}>
              Email Sequence Management
            </h2>
            <p className="text-gray-600">
              Manage email templates and delays for all sequences
            </p>
          </div>

          {/* Initialize Button */}
          <div className="mb-8">
            <button
              onClick={handleInitialize}
              disabled={initializing}
              className="px-6 py-3 rounded-lg font-semibold text-white shadow-lg hover:shadow-xl transition disabled:opacity-50"
              style={{backgroundColor: '#8B3A9B'}}
            >
              {initializing ? 'Initializing...' : 'Initialize All Email Templates'}
            </button>
            <p className="text-sm text-gray-500 mt-2">
              Creates placeholder templates for all 60 emails (5 sequences × 12 emails)
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8">
            {/* Left Sidebar - Sequence & Email List */}
            <div className="lg:col-span-3">
              {/* Sequence Selector */}
              <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <h3 className="font-bold text-lg mb-4" style={{color: '#C85A3E'}}>
                  Select Sequence
                </h3>
                <select
                  value={selectedSequence}
                  onChange={(e) => setSelectedSequence(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
                  data-testid="sequence-selector"
                >
                  {sequences.map(seq => (
                    <option key={seq.value} value={seq.value}>
                      {seq.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Email List */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="font-bold text-lg mb-4" style={{color: '#D4A948'}}>
                  Emails ({templates.length})
                </h3>
                
                {loading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                  </div>
                ) : templates.length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    No templates found. Click "Initialize All Email Templates" to create them.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {templates.map((template) => (
                      <button
                        key={template.emailNumber}
                        onClick={() => selectEmail(template)}
                        className={`w-full text-left px-4 py-3 rounded-lg transition ${
                          selectedEmail?.emailNumber === template.emailNumber
                            ? 'bg-teal-100 border-2 border-teal-500'
                            : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                        }`}
                        data-testid={`email-${template.emailNumber}`}
                      >
                        <div className="font-semibold">Email {template.emailNumber}</div>
                        <div className="text-xs text-gray-600 truncate">
                          {template.subject}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Main Editor */}
            <div className="lg:col-span-9">
              {selectedEmail ? (
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold" style={{color: '#2C7A9F'}}>
                      Edit Email {selectedEmail.emailNumber}
                    </h3>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-6 py-2 rounded-lg font-semibold text-white shadow-lg hover:shadow-xl transition disabled:opacity-50"
                      style={{backgroundColor: '#2C7A9F'}}
                      data-testid="save-button"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* Subject */}
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Subject Line *
                      </label>
                      <input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
                        placeholder="Enter email subject"
                        data-testid="subject-input"
                      />
                    </div>

                    {/* Content */}
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Email Content *
                      </label>
                      <textarea
                        value={formData.content}
                        onChange={(e) => setFormData({...formData, content: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
                        rows="12"
                        placeholder="Enter email content"
                        data-testid="content-textarea"
                      />
                      <p className="text-sm text-gray-500 mt-2">
                        Use {'{'}{'{'} firstName {'}'}{'}'}  to personalize with the subscriber's first name
                      </p>
                    </div>

                    {/* Delay Hours */}
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Delay (Hours) *
                      </label>
                      <input
                        type="number"
                        value={formData.delayHours}
                        onChange={(e) => setFormData({...formData, delayHours: parseInt(e.target.value)})}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
                        min="1"
                        data-testid="delay-input"
                      />
                      <p className="text-sm text-gray-500 mt-2">
                        Hours to wait before sending this email (after previous email or enrollment)
                      </p>
                    </div>

                    {/* Button Text */}
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Call-to-Action Button Text *
                      </label>
                      <input
                        type="text"
                        value={formData.buttonText}
                        onChange={(e) => setFormData({...formData, buttonText: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
                        placeholder="e.g., Yes I want the magazine"
                        data-testid="button-text-input"
                      />
                    </div>

                    {/* Button Link */}
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Button Link (URL) *
                      </label>
                      <input
                        type="text"
                        value={formData.buttonLink}
                        onChange={(e) => setFormData({...formData, buttonLink: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
                        placeholder="e.g., /offer-1"
                        data-testid="button-link-input"
                      />
                    </div>

                    {/* Preview */}
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6 border-2 border-gray-200">
                      <h4 className="font-bold text-lg mb-4" style={{color: '#8B3A9B'}}>
                        Preview
                      </h4>
                      <div className="bg-white rounded-lg p-6 shadow-inner">
                        <div className="text-sm text-gray-600 mb-2">Subject:</div>
                        <div className="font-bold text-lg mb-4">{formData.subject || '(No subject)'}</div>
                        <div className="text-sm text-gray-600 mb-2">Content:</div>
                        <div className="whitespace-pre-wrap mb-4">{formData.content || '(No content)'}</div>
                        {formData.buttonText && (
                          <button
                            className="px-6 py-3 rounded-lg font-semibold text-white"
                            style={{backgroundColor: '#2C7A9F'}}
                            disabled
                          >
                            {formData.buttonText}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                  <svg className="w-24 h-24 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <h3 className="text-2xl font-bold mb-2 text-gray-700">No Email Selected</h3>
                  <p className="text-gray-600">
                    {templates.length === 0 
                      ? 'Initialize email templates to get started'
                      : 'Select an email from the list to edit'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminPanel;
